import { ApiError, transportProblem, type Problem } from "@baton/api";
import { useI18n } from "@baton/i18n";
import { Copy } from "@baton/icons";
import { Button, ErrorState } from "@baton/ui";
import { useState } from "react";
import { isRouteErrorResponse, useLocation, useNavigate, useParams, useRouteError } from "react-router";

/**
 * FR-015-090, `contracts/design-system.md` § Error, `contracts/screens.md` § Error and utility
 * screens (the Screen failure row, 2026-10-10): a screen that throws during render renders the
 * `error` state inside the shell, with Retry re-entering the route and Go to my work leaving it,
 * and never a stack trace or a raw body. What it says follows what was thrown. A refused request
 * (`ApiError`) keeps the server's title, its detail and its request id with the copy control, so a
 * 403 or a 500 that broke the screen reads as the same refusal would in a panel. A route error
 * response carries its status and nothing came back over the wire, so its problem is synthetic.
 * Anything else is a defect in the screen itself: a generic title and body, the error's own message
 * as the detail in development only, and Copy details, which puts the error's name and message and
 * the address on the clipboard for a report, never its stack. Until 2026-10-10 every throw was
 * drawn as "Request failed with status 0", the server's words and the request id lost with it.
 */

/** A throw that is neither a refused request nor a route error response is a defect of the screen itself. */
function isDefect(error: unknown): boolean {
  return !(error instanceof ApiError) && !isRouteErrorResponse(error);
}

function routeProblem(error: unknown, words: { readonly title: string; readonly body: string }, dev: boolean): Problem {
  if (error instanceof ApiError) return error.problem;
  if (isRouteErrorResponse(error)) return transportProblem(error.status, "");
  const message = error instanceof Error ? error.message : "";
  // The synthetic problem of a defect: no request was refused, so its type names the render and it carries no request id.
  return { type: "render", title: words.title, status: 0, request_id: "", detail: dev && message.length > 0 ? message : words.body };
}

/** What Copy details puts on the clipboard: the error's name and message and where it happened, never its stack. */
function detailsOf(error: unknown, pathname: string): string {
  const line = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  return `${line}\nat ${pathname}`;
}

export function RouteError() {
  const { t } = useI18n();
  const error = useRouteError();
  const navigate = useNavigate();
  const { ws } = useParams();
  const { pathname } = useLocation();
  const [copied, setCopied] = useState(false);
  const defect = isDefect(error);
  const problem = routeProblem(error, { title: t("routeError.title"), body: t("routeError.body") }, import.meta.env.DEV);
  return (
    <div className="b-page">
      <ErrorState form="full-page" problem={problem} onRetry={() => void navigate(0)} />
      {(defect || ws !== undefined) && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {defect && (
            <Button
              size="sm"
              variant="ghost"
              iconStart={<Copy />}
              onClick={() => {
                navigator.clipboard
                  .writeText(detailsOf(error, pathname))
                  .then(() => {
                    setCopied(true);
                  })
                  .catch(() => undefined);
              }}
            >
              {copied ? t("ui.copied") : t("routeError.copyDetails")}
            </Button>
          )}
          {ws !== undefined && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void navigate(`/${ws}/my-work`);
              }}
            >
              {t("notFound.goToMyWork")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
